'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  enterpriseLogin,
  fetchEnterpriseTenants,
  loginUser,
  logoutUser,
  setUserAsAdmin,
  updateUserLocation,
} from '@/lib/api/auth';

import { paths } from '@/constants/paths';
import { QueryKeys } from '@/constants/query-keys';
import { LoginSchema } from '@/schema/auth';


export function useEnterpriseFetchTenants() {
  const t = useTranslations('AuthMutations');

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      fetchEnterpriseTenants(email, password),

    onSuccess: (tenants, { email, password }) => {
      // Clear stale tenant_id cookie so middleware doesn't intercept /auth/workspace
      document.cookie = 'tenant_id=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
      sessionStorage.setItem('enterprise_tenants', JSON.stringify(tenants));
      sessionStorage.setItem('enterprise_email', email);
      sessionStorage.setItem('enterprise_password', password);
      window.location.href = paths.auth.workspace;
    },
    onError: (error) => {
      toast.error(t('enterpriseNoWorkspacesTitle'), {
        description: error.message || t('enterpriseNoWorkspacesDescription'),
      });
    },
  });
}

export function useEnterpriseWorkspaceLogin() {
  const t = useTranslations('AuthMutations');

  return useMutation({
    mutationFn: enterpriseLogin,
    onSuccess: (data) => {
      toast.success(t('loginSuccessTitle'), {
        description: t('loginSuccessDescription'),
      });
      const isAdmin = data.user?.user_metadata?.is_admin;
      // const userLocationId = data.user?.user_metadata?.location_id;
      const redirectPath =  paths.locations.select;
      window.location.href = redirectPath;
    },
    onError: (error) => {
      toast.error(t('loginFailedTitle'), {
        description: error.message || t('loginFailedDescription'),
      });
    },
  });
}

export function useSwitchWorkspace() {
  const t = useTranslations('AuthMutations');

  return useMutation({
    mutationFn: logoutUser,
    onSuccess: () => {
      window.location.href = paths.auth.workspace;
    },
    onError: (error) => {
      toast.error(t('switchWorkspaceFailedTitle'), {
        description: error.message || t('switchWorkspaceFailedDescription'),
      });
    },
  });
}

export function useLogin() {
  const router = useRouter();
  const t = useTranslations('AuthMutations');
  const [waking, setWaking] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: LoginSchema) =>
      loginUser(data, {
        onWakingStart: () => setWaking(true),
        onWakingEnd: () => setWaking(false),
      }),
    onSuccess: (data) => {
      toast.success(t('loginSuccessTitle'), {
        description: t('loginSuccessDescription'),
      });

      // Redirect based on user type and location
      // Use window.location.href for full page reload to ensure middleware can read cookies
      const isAdmin = data.user?.user_metadata?.is_admin;
      const userLocationId = data.user?.user_metadata?.location_id;

      let redirectPath: string;
      if (isAdmin) {
        // Admin users go directly to calendar
        redirectPath = paths.pos.calendar;
      } else if (userLocationId) {
        // Staff users with location go to calendar
        redirectPath = paths.pos.calendar;
      } else {
        // Staff users without location go to location select
        redirectPath = paths.locations.select;
      }

      // Use full page reload to ensure middleware runs and can read cookies
      window.location.href = redirectPath;
    },
    onError: (error) => {
      toast.error(t('loginFailedTitle'), {
        description: error.message || t('loginFailedDescription'),
      });
    },
  });

  return { ...mutation, waking };
}

export function useLogout() {
  const queryClient = useQueryClient();
  const t = useTranslations('AuthMutations');

  return useMutation({
    mutationFn: logoutUser,
    onSuccess: () => {
      toast.success(t('logoutSuccess'));
      queryClient.clear();
      sessionStorage.clear();
      // Clear persisted Zustand stores and legacy calendar filter keys
      [
        'pos-page-location-filters',
        'pos-calendar-staff-filter-exclusions',
        'pos-calendar-staff-filter-v1',
        'pos-calendar-staff-filter',
      ].forEach((key) => localStorage.removeItem(key));
      window.location.href = paths.auth.login;
    },
    onError: (error) => {
      toast.error(t('logoutFailedTitle'), {
        description: error.message || t('logoutFailedDescription'),
      });
    },
  });
}

export function useUpdateUserLocation() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const t = useTranslations('AuthMutations');

  return useMutation({
    mutationFn: updateUserLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CURRENT_LOCATION] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.TEAM_MEMBERS] });
      toast.success(t('locationSelectedSuccess'));
      router.push(paths.pos.calendar);
    },
    onError: (error) => {
      toast.error(t('locationSaveFailedTitle'), {
        description: error.message || t('locationSaveFailedDescription'),
      });
    },
  });
}

export function useSetUserAsAdmin() {
  const t = useTranslations('AuthMutations');

  return useMutation({
    mutationFn: setUserAsAdmin,
    onSuccess: () => {
      toast.success(t('adminUpdatedSuccess'));
    },
    onError: (error) => {
      toast.error(t('adminUpdateFailedTitle'), {
        description: error.message || t('adminUpdateFailedDescription'),
      });
    },
  });
}
